import { CheckIcon, ShareIcon, XIcon } from '@phosphor-icons/react'
import { mappedColors, typography } from '@pixee/design-system'
import { DateTime } from 'luxon'
import { match } from 'ts-pattern'
import type { PolarisStatusResponse, PushResponse, SkippedEntry } from '../../utils/api-client/api-schemas'
import { formatDate } from '../../utils/date-utils'
import { getPolarisSkipReasonPhrase, POLARIS_STATUS_UNAVAILABLE_MESSAGE } from '../../utils/polaris-status'
import { TooltipWithHover } from '../tooltip'

type PushStatusIconProps = {
  pushState?: PushResponse
  size?: number
}

export const PushStatusIcon: React.FC<PushStatusIconProps> = ({ pushState, size = 14 }) => {
  const isLarge = size >= 18
  const shareWeight = isLarge ? 'light' : 'thin'
  const overlayWeight = 'regular'
  const overlaySize = Math.round(size * (8 / 14))

  return match(pushState?.status)
    .with('SUCCESS', () => (
      <span style={{ position: 'relative', display: 'inline-flex' }}>
        <ShareIcon size={size} weight={shareWeight} color={mappedColors.neutral.neutral30} />
        <CheckIcon
          size={overlaySize}
          weight={overlayWeight}
          color={mappedColors.outcomeBanner.successIcon}
          style={{ position: 'absolute', top: -2, left: -4 }}
        />
      </span>
    ))
    .with('FAILED', () => (
      <span style={{ position: 'relative', display: 'inline-flex' }}>
        <ShareIcon size={size} weight={shareWeight} color={mappedColors.neutral.neutral30} />
        <XIcon
          size={overlaySize}
          weight={overlayWeight}
          color={mappedColors.secondary.spark}
          style={{ position: 'absolute', top: -2, left: -4 }}
        />
      </span>
    ))
    .with('IN_PROGRESS', () => <ShareIcon size={size} weight={shareWeight} color={mappedColors.neutral.neutral30} />)
    .otherwise(() => <ShareIcon size={size} weight={shareWeight} color="currentColor" />)
}

/**
 * What Polaris has to say about one finding, once someone has asked.
 *
 * A union rather than a set of independent flags so that "the lookup failed", "Polaris has no issue
 * for this finding" and "Polaris reported a status" cannot describe the same badge at once. `undefined`
 * covers the state before anyone hovers, which is the common one: the lookup is deferred until then.
 */
export type PolarisStatusView =
  | { state: 'pending' }
  | { state: 'unavailable' }
  | { state: 'resolved'; status: PolarisStatusResponse }
  | { state: 'skipped'; skipped: SkippedEntry }

type PushStatusBadgeProps = {
  pushState?: PushResponse
  integrationName?: string
  /** What Polaris currently says. `undefined` until something asks, and for non-Polaris integrations. */
  polarisStatusView?: PolarisStatusView
  /**
   * Called when the badge is first hovered or focused, so the owner can start the Polaris lookup.
   *
   * Absent when there is nothing to look up, which is also what decides whether the badge is
   * focusable. Prop-driven rather than fetched here so the component stays renderable from plain data:
   * it sits inside `ScanFindingsTablePure`, whose whole contract is that its subtree does no I/O.
   */
  onReveal?: () => unknown
}

/** Longest comment shown inline, chosen to keep the tooltip a few lines rather than a wall. */
export const MAX_COMMENT_LENGTH = 110

/**
 * Condenses a Polaris triage comment for display in a tooltip.
 *
 * Polaris comments are routinely Pixee's own pushed analysis: `PolarisPushService` writes the triage
 * summary followed by a link back to this app, so rendering the raw value filled the tooltip with our
 * own multi-paragraph text and a localhost URL. Keeps the first paragraph, drops the markdown code
 * ticks that render literally here, flattens the newlines and truncates on a word boundary.
 */
const summarizeComment = (comment: string) => {
  const [firstParagraph = ''] = comment.split(/\n\s*\n/)
  const flattened = firstParagraph
    .replace(/`/g, '')
    .replace(/\s+/g, ' ')
    .replace(/^See full analysis in Pixee:.*$/, '')
    .trim()
  if (flattened.length <= MAX_COMMENT_LENGTH) {
    return flattened || undefined
  }
  const truncated = flattened.slice(0, MAX_COMMENT_LENGTH)
  const lastSpace = truncated.lastIndexOf(' ')
  return `${(lastSpace > MAX_COMMENT_LENGTH / 2 ? truncated.slice(0, lastSpace) : truncated).trimEnd()}…`
}

const formatPolarisDetail = (status: PolarisStatusResponse) =>
  match(status.state)
    .with('lookup-failed', () => status.error_reason ?? POLARIS_STATUS_UNAVAILABLE_MESSAGE)
    .with('not-found', () => 'Finding not found in Polaris')
    .with('not-applicable', () => undefined)
    .with('match', 'conflict', state => {
      // A conflict is the whole reason this tooltip exists, so it must not read identically to a
      // match — the difference is what tells the user a push would overwrite someone's decision.
      const prefix = state === 'conflict' ? 'Differs from Polaris' : 'Matches Polaris'
      const details = [prefix, status.current_status]
      if (status.acknowledged_by) {
        details.push(`acknowledged by ${status.acknowledged_by}`)
      }
      if (status.acknowledged_at) {
        details.push(formatDate({ date: status.acknowledged_at, type: 'mm/dd/yyyy-hh:mm' }))
      }
      // Only on a conflict. When Polaris agrees with us the comment is usually the one Pixee wrote
      // on a previous push, so echoing it back says nothing the user did not already know. It earns
      // its space only when someone else recorded a different decision and explained why.
      if (state === 'conflict' && status.comment) {
        details.push(summarizeComment(status.comment))
      }
      return details.filter(detail => !!detail).join(' · ')
    })
    .exhaustive()

/** Explains a target the lookup left out, so the tooltip says why rather than saying nothing. */
const formatPolarisSkip = (skipped: SkippedEntry) =>
  `Not checked in Polaris because ${getPolarisSkipReasonPhrase(skipped.reason)}`

/**
 * Joins the push's own outcome with Polaris's current state.
 *
 * Both are kept: a failed push's `error_reason` explains why nothing was written, which the Polaris
 * status does not replace, and dropping it left a failed push looking like a successful one whose
 * status merely differs.
 */
const composeDetail = (pushErrorReason: string | undefined, polarisDetail: string | undefined) =>
  [pushErrorReason, polarisDetail].filter(detail => !!detail).join(' — ') || undefined

/**
 * The push indicator, and on hover what Polaris currently says about the same finding.
 *
 * Takes the resolved Polaris view rather than fetching it. The lookup is hover-gated, so its owner
 * needs to know which badge was reached: `ScanFindingsTable` holds that state and the query, and
 * passes the answer back down. That keeps `ScanFindingsTablePure` drivable from plain props, which is
 * the point of the split, and keeps one request in flight rather than one per visible row.
 */
export const PushStatusBadge: React.FC<PushStatusBadgeProps> = ({
  pushState,
  integrationName,
  polarisStatusView,
  onReveal,
}) => {
  const target = integrationName ? ` to ${integrationName}` : ''
  const timestamp = pushState?.updated_at ?? pushState?.created_at
  const relative = timestamp ? DateTime.fromISO(timestamp).toRelative() : null

  const polarisDetail = match(polarisStatusView)
    // Nobody has hovered yet, or this integration has no Polaris state to report.
    .with(undefined, () => undefined)
    .with({ state: 'pending' }, () => 'Checking current Polaris status…')
    .with({ state: 'unavailable' }, () => POLARIS_STATUS_UNAVAILABLE_MESSAGE)
    .with({ state: 'resolved' }, ({ status }) => formatPolarisDetail(status))
    .with({ state: 'skipped' }, ({ skipped }) => formatPolarisSkip(skipped))
    .exhaustive()

  const label = match(pushState?.status)
    .with('SUCCESS', () => `Pushed${target}`)
    .with('IN_PROGRESS', () => `Push${target} in progress`)
    .with('FAILED', () => 'Push failed')
    .otherwise(() => `Push classification${target}`)

  return (
    <IconTooltip
      icon={<PushStatusIcon pushState={pushState} />}
      label={label}
      relative={relative}
      detail={composeDetail(pushState?.error_reason, polarisDetail)}
      onReveal={onReveal}
    />
  )
}

type IconTooltipProps = {
  icon: React.ReactNode
  label: string
  relative: string | null
  detail?: string
  onReveal?: () => unknown
}

const IconTooltip: React.FC<IconTooltipProps> = ({ icon, label, relative, detail, onReveal }) => (
  <TooltipWithHover
    side="top"
    maxWidth={260}
    trigger={
      <span
        role="img"
        aria-label={relative ? `${label} ${relative}` : label}
        style={{ display: 'inline-flex', lineHeight: 0 }}
        // Focus as well as hover: Radix opens the tooltip on keyboard focus, so gating the lookup on
        // the mouse alone left keyboard users with a tooltip that never filled in.
        onMouseEnter={onReveal}
        onFocus={onReveal}
        tabIndex={onReveal ? 0 : undefined}
      >
        {icon}
      </span>
    }
  >
    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}>
        <span className={typography.extraSmall} style={{ color: mappedColors.neutral.neutral10 }}>
          {label}
        </span>
        {relative && (
          <span
            className={typography.extraSmall}
            style={{ color: mappedColors.neutral.neutral30, fontStyle: 'italic' }}
          >
            {relative}
          </span>
        )}
      </div>
      {detail && (
        <span className={typography.extraSmall} style={{ color: mappedColors.neutral.neutral30 }}>
          {detail}
        </span>
      )}
    </div>
  </TooltipWithHover>
)
